import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../utils/api';

interface BookInfo {
  id: number;
  title: string;
  author: string;
  description?: string;
  cover?: string;
  format: string;
  viewCount: number;
  rating: number;
  uploadedAt: string;
}

interface Bookshelf {
  id: number;
  name: string;
  bookCount: number;
}

const BookDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  
  const [book, setBook] = useState<BookInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [bookshelves, setBookshelves] = useState<Bookshelf[]>([]);
  const [selectedBookshelf, setSelectedBookshelf] = useState<number | null>(null);
  const [newBookshelfName, setNewBookshelfName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [favoritedBookshelfIds, setFavoritedBookshelfIds] = useState<number[]>([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  useEffect(() => {
    fetchBookDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBookDetail = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/books/${id}`);
      setBook(data);
    } catch (error) {
      console.error('获取图书详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 检查图书是否已被收藏
  const checkFavoriteStatus = async () => {
    if (!isAuthenticated || !user) return;
    try {
      const { data } = await api.get('/bookshelves/books/check', {
        params: { bookId: id }
      });
      setFavoritedBookshelfIds(data.bookshelfIds || []);
    } catch (error) {
      console.error('检查收藏状态失败:', error);
    }
  };

  // 获取用户书单
  const fetchBookshelves = async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await api.get('/bookshelves/my');
      setBookshelves(data || []);
    } catch (error) {
      console.error('获取书单失败:', error);
    }
  };

  const handleFavoriteClick = async () => {
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }
    
    await fetchBookshelves();
    await checkFavoriteStatus();
    setShowFavoriteModal(true);
  };

  // 创建新书单并添加图书
  const handleCreateAndAdd = async () => {
    if (!newBookshelfName.trim()) return;
    setFavoriteLoading(true);
    try {
      // 创建书单
      const { data: newBookshelf } = await api.post('/bookshelves', {
        name: newBookshelfName.trim()
      });
      
      // 添加图书到书单
      await api.post('/bookshelves/books', {
        bookshelfId: newBookshelf.id,
        bookId: id
      });
      
      setShowFavoriteModal(false);
      setNewBookshelfName('');
      setShowCreateForm(false);
      alert('收藏成功！');
    } catch (error: any) {
      alert(error.response?.data?.message || '操作失败');
    } finally {
      setFavoriteLoading(false);
    }
  };

  // 添加到已选书单
  const handleAddToBookshelf = async () => {
    if (!selectedBookshelf) return;
    setFavoriteLoading(true);
    try {
      await api.post('/bookshelves/books', {
        bookshelfId: selectedBookshelf,
        bookId: id
      });
      setShowFavoriteModal(false);
      setSelectedBookshelf(null);
      alert('收藏成功！');
    } catch (error: any) {
      alert(error.response?.data?.message || '操作失败');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleRead = () => {
    history.push(`/read/${id}`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <p>图书不存在</p>
      </div>
    );
  }

  // 获取未收藏的书单
  const availableBookshelves = bookshelves.filter(b => !favoritedBookshelfIds.includes(b.id));

  return (
    <div>
      <div style={{ display: 'flex', gap: '40px', marginBottom: '40px' }}>
        <div style={{ flexShrink: 0 }}>
          {book.cover ? (
            <img 
              src={book.cover} 
              alt={book.title} 
              style={{ 
                width: '240px', 
                height: '340px', 
                objectFit: 'cover',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }} 
            />
          ) : (
            <div 
              style={{ 
                width: '240px', 
                height: '340px', 
                background: '#f0f0f0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}
            >
              暂无封面
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', marginBottom: '12px' }}>{book.title}</h1>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
            作者：{book.author}
          </p>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button onClick={handleRead} className="btn btn-primary" style={{ padding: '12px 32px', fontSize: '16px' }}>
              开始阅读
            </button>
            <button 
              onClick={handleFavoriteClick} 
              className="btn"
              style={{ 
                padding: '12px 24px',
                background: favoritedBookshelfIds.length > 0 ? '#faad14' : '#fff',
                border: '1px solid #faad14',
                color: favoritedBookshelfIds.length > 0 ? '#fff' : '#faad14'
              }}
            >
              {favoritedBookshelfIds.length > 0 ? '已收藏' : '收藏'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '24px', color: '#666' }}>
            <span>格式：{book.format.toUpperCase()}</span>
            <span>阅读：{book.viewCount}</span>
            <span>评分：{book.rating > 0 ? book.rating.toFixed(1) : '暂无'}</span>
          </div>
        </div>
      </div>

      {book.description && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '12px' }}>简介</h3>
          <p style={{ lineHeight: '1.8', color: '#666' }}>{book.description}</p>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>评论</h3>
        <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
          暂无评论，快来抢先评论吧！
        </p>
      </div>

      {/* 收藏弹窗 */}
      {showFavoriteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', maxHeight: '80vh', overflow: 'auto', position: 'relative', padding: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>添加到书单</h3>
            
            {!showCreateForm ? (
              <>
                {availableBookshelves.length > 0 ? (
                  <>
                    <p style={{ marginBottom: '12px', color: '#666' }}>选择书单：</p>
                    <div style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '16px' }}>
                      {availableBookshelves.map(shelf => (
                        <label 
                          key={shelf.id}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '8px',
                            cursor: 'pointer',
                            background: selectedBookshelf === shelf.id ? '#e6f7ff' : 'transparent',
                            borderRadius: '4px',
                            marginBottom: '4px'
                          }}
                        >
                          <input
                            type="radio"
                            name="bookshelf"
                            checked={selectedBookshelf === shelf.id}
                            onChange={() => setSelectedBookshelf(shelf.id)}
                            style={{ marginRight: '8px' }}
                          />
                          <span>{shelf.name}</span>
                          <span style={{ color: '#999', marginLeft: '8px' }}>({shelf.bookCount}本)</span>
                        </label>
                      ))}
                    </div>
                    <button 
                      onClick={handleAddToBookshelf}
                      disabled={!selectedBookshelf || favoriteLoading}
                      className="btn btn-primary"
                      style={{ width: '100%', marginBottom: '8px' }}
                    >
                      {favoriteLoading ? '添加中...' : '添加到选中的书单'}
                    </button>
                  </>
                ) : (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                    暂无可用的书单
                  </p>
                )}
                
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="btn btn-default"
                  style={{ width: '100%' }}
                >
                  + 创建新书单
                </button>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>书单名称：</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newBookshelfName}
                    onChange={(e) => setNewBookshelfName(e.target.value)}
                    placeholder="请输入书单名称"
                    autoFocus
                  />
                </div>
                <button 
                  onClick={handleCreateAndAdd}
                  disabled={!newBookshelfName.trim() || favoriteLoading}
                  className="btn btn-primary"
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  {favoriteLoading ? '创建中...' : '创建并添加'}
                </button>
                <button 
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewBookshelfName('');
                  }}
                  className="btn btn-default"
                  style={{ width: '100%' }}
                >
                  返回
                </button>
              </>
            )}
            
            <button 
              onClick={() => {
                setShowFavoriteModal(false);
                setSelectedBookshelf(null);
                setShowCreateForm(false);
                setNewBookshelfName('');
              }}
              style={{ 
                position: 'absolute',
                top: '10px',
                right: '10px',
                border: 'none',
                background: 'transparent',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookDetail;
